import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from '@tanstack/react-query';
import cn from 'clsx';
import { Button, Dropdown, Modal, Tooltip } from 'flowbite-react';
import { usePostHog } from 'posthog-js/react';
import { useState } from 'react';
import { InlineWidget } from 'react-calendly';
import { Link, Outlet, useLoaderData, useMatches } from 'react-router-dom';

import { LoaderData, loaders } from 'src/api/loaders.ts';
import type { KlaviyoAccount, ShopifyShop, Store, User, UserProfile } from 'src/api/models';
import { queries } from 'src/api/queries.ts';
import MaverickLogo from 'src/assets/img/maverick-wordmark-new-large.svg?react';

export interface DashboardContext {
  user: User;
  store: Store | undefined;
  klaviyoAccount: KlaviyoAccount | undefined;
  shopifyShop: ShopifyShop | undefined;
  userProfile: UserProfile | undefined;
  usecases: LoaderData<typeof loaders.usecasesForStoreLoader>;
}

export function DashboardContainer() {
  const posthog = usePostHog();
  const renderContext = useLoaderData() as DashboardContext;
  const matches = useMatches();
  const storeKeyName = renderContext.store?.key_name;
  const {
    data: { results: usecases },
  } = useQuery({
    ...queries.usecasesForStore(storeKeyName),
    initialData: renderContext.usecases,
    enabled: !!storeKeyName,
  });
  const [openModal, setOpenModal] = useState(false);
  return (
    <>
      <header>
        <nav className='fixed left-0 right-0 top-0 z-30 w-full border-b border-gray-200 bg-white'>
          <div className='px-3 py-2 lg:px-5 lg:pl-3'>
            <div className='flex items-center justify-between p-1'>
              <div className='flex items-center justify-start'>
                <button
                  id='toggleSidebarMobile'
                  aria-expanded='true'
                  aria-controls='sidebar'
                  className='m-2 cursor-pointer rounded text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:bg-gray-100 focus:ring-2 focus:ring-gray-100 lg:hidden'
                >
                  <FontAwesomeIcon icon={solid('bars')} />
                  <FontAwesomeIcon icon={solid('xmark')} className='hidden' />
                </button>
                <Link to='' className='ml-2 flex md:mr-24'>
                  <MaverickLogo className='mr-3 h-8' />
                </Link>
              </div>
              <div className='flex items-center'>
                <span className='mr-4'>{renderContext.store?.company_name}</span>
                <Dropdown
                  label='Profile'
                  dismissOnClick={false}
                  renderTrigger={() => (
                    <div>
                      <span className='sr-only'>Open user menu</span>
                      <img
                        className='h-8 w-8 rounded-full'
                        src='https://i.pinimg.com/236x/70/a9/e1/70a9e11f2cd03981d9788f1250000925.jpg'
                        alt='user photo'
                      ></img>
                    </div>
                  )}
                >
                  <Dropdown.Item as='span' className='hover:bg-none'>
                    {renderContext.user.email}
                  </Dropdown.Item>
                  <Dropdown.Item
                    as={Link}
                    to='/accounts/logout/'
                    reloadDocument
                    className='block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100'
                    role='menuitem'
                    onClick={() => posthog.reset()}
                  >
                    Sign out
                  </Dropdown.Item>
                </Dropdown>
              </div>
            </div>
          </div>
        </nav>
      </header>
      <div className='flex min-h-screen overflow-hidden bg-gray-50 pt-14'>
        <aside
          id='sidebar'
          className='transition-width fixed left-0 top-0 z-20 mt-14 hidden h-full w-64 flex-shrink-0 flex-col font-normal duration-75 lg:flex'
          aria-label='Sidebar'
        >
          <div className='relative flex min-h-0 flex-1 flex-col border-r border-gray-200 bg-white pt-0'>
            <div className='flex flex-1 flex-col overflow-y-auto pb-4 pt-5'>
              <div className='flex-1 space-y-1 divide-y divide-gray-200 bg-white px-3'>
                <ul className='space-y-2 pb-2'>
                  <li>
                    <Tooltip
                      placement='right'
                      className={
                        renderContext.store && usecases?.length ? 'hidden group-hover:block' : ''
                      }
                      content={
                        renderContext.store && usecases?.length
                          ? ''
                          : renderContext.store
                            ? 'Fill out Flows to unlock'
                            : 'Fill out Store Info to unlock'
                      }
                    >
                      <Link
                        to=''
                        className={cn(
                          'group flex items-center rounded-lg p-2 text-base ',
                          renderContext.store && usecases?.length
                            ? 'text-gray-900 hover:bg-gray-100'
                            : 'pointer-events-none text-gray-400',
                          { 'bg-gray-200': matches[matches.length - 1]?.id === 'home' },
                        )}
                      >
                        <FontAwesomeIcon icon={solid('house')} fixedWidth />
                        <span className='ml-3'>Home</span>
                      </Link>
                    </Tooltip>
                  </li>
                  <li>
                    <Tooltip
                      placement='right'
                      className={renderContext.store ? 'hidden group-hover:block' : ''}
                      content={renderContext.store ? '' : 'Fill out Store Info to unlock'}
                    >
                      <Link
                        to={renderContext.store ? 'video-flows/' : ''}
                        className={cn(
                          'group flex items-center rounded-lg p-2 text-base ',
                          renderContext.store
                            ? 'text-gray-900 hover:bg-gray-100'
                            : 'pointer-events-none text-gray-400',
                          {
                            'bg-gray-200': ['video-flows', 'video-flows-usecase'].includes(
                              matches[matches.length - 1]?.id ?? '',
                            ),
                          },
                        )}
                      >
                        <FontAwesomeIcon icon={solid('film')} fixedWidth />
                        <span className='ml-3'>Flows</span>
                        {usecases &&
                          usecases.some(
                            (u) => !['LIVE', 'STOPPED'].includes(u.onboarding_state),
                          ) && (
                            <span className='ml-2 inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-200 text-xs font-semibold text-red-800'>
                              !
                            </span>
                          )}
                      </Link>
                    </Tooltip>
                  </li>
                  <li>
                    <Tooltip
                      placement='right'
                      className={
                        renderContext.store && usecases?.length ? 'hidden group-hover:block' : ''
                      }
                      content={
                        renderContext.store && usecases?.length
                          ? ''
                          : renderContext.store
                            ? 'Fill out Flows to unlock'
                            : 'Fill out Store Info to unlock'
                      }
                    >
                      <Link
                        to={renderContext.store && usecases?.length ? 'integrations/' : ''}
                        className={cn(
                          'group flex items-center rounded-lg p-2 text-base ',
                          renderContext.store && usecases?.length
                            ? 'text-gray-900 hover:bg-gray-100'
                            : 'pointer-events-none text-gray-400',
                          { 'bg-gray-200': matches[matches.length - 1]?.id === 'integrations' },
                        )}
                      >
                        <FontAwesomeIcon icon={solid('link')} fixedWidth />
                        <span className='ml-3'>Integrations</span>
                        {renderContext.store &&
                          usecases &&
                          usecases.length > 0 &&
                          ((!renderContext.klaviyoAccount && !renderContext.shopifyShop) ||
                            !renderContext.shopifyShop?.access_scope_is_current ||
                            renderContext.shopifyShop?.error_message) && (
                            <span className='ml-2 inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-200 text-xs font-semibold text-red-800'>
                              !
                            </span>
                          )}
                      </Link>
                    </Tooltip>
                  </li>
                  <li>
                    <Link
                      to={renderContext.store ? 'store/' : ''}
                      style={{ pointerEvents: renderContext.store ? undefined : 'none' }}
                      className={cn(
                        'group flex items-center rounded-lg p-2 text-base text-gray-900 hover:bg-gray-100',
                        { 'pointer-events-none': !renderContext.store },
                        { 'bg-gray-200': matches[matches.length - 1]?.id === 'store' },
                      )}
                    >
                      <FontAwesomeIcon icon={solid('store')} fixedWidth />
                      <span className='ml-3'>Store Info</span>
                    </Link>
                  </li>
                  <li>
                    {renderContext.userProfile &&
                      renderContext.userProfile.state === 'NEW_USER' && (
                        <Button color='dark' className='w-44' onClick={() => setOpenModal(true)}>
                          Book a call
                        </Button>
                      )}
                  </li>
                  <Modal dismissible show={openModal} onClose={() => setOpenModal(false)}>
                    <Modal.Header>Schedule a Call!</Modal.Header>
                    <Modal.Body>
                      <div className='App'>
                        <InlineWidget url='https://calendly.com/d/5f8-nrk-xrj' />
                      </div>
                    </Modal.Body>
                  </Modal>
                </ul>
              </div>
            </div>
          </div>
        </aside>
        <div id='main-content' className='relative h-auto w-full overflow-y-auto p-3 lg:ml-64'>
          <main className='h-full w-full'>
            <Outlet context={renderContext} />
          </main>
        </div>
      </div>
    </>
  );
}
